import { tempoApi } from '@/lib/api/tempoApi';
import type {
  CreateInstanceFieldMappingRequest,
  CreateWorkIntegrationInstanceRequest,
  FieldMappingSummary,
  FieldMappingUsage,
  FieldValueMapping,
  InstanceFieldMapping,
  JiraFieldValue,
  LoopFieldValue,
  SaveFieldValueMappingsRequest,
  UpdateInstanceFieldMappingRequest,
  WorkIntegration_Keys,
  WorkIntegrationInstance,
} from '@/lib/api/types/work-integrations';

const createInstance = async (
  payload: CreateWorkIntegrationInstanceRequest,
): Promise<WorkIntegrationInstance> => {
  const response = await tempoApi()
    .post('api/v1/work-integrations/instances', {
      json: payload,
    })
    .json<WorkIntegrationInstance>();
  return response;
};

const getInstances = async (
  sourceSystemType?: WorkIntegration_Keys,
): Promise<WorkIntegrationInstance[]> => {
  const searchParams = new URLSearchParams();
  if (sourceSystemType) {
    searchParams.append('sourceSystemType', sourceSystemType);
  }
  const url = `api/v1/work-integrations/instances${searchParams.toString() ? `?${searchParams.toString()}` : ''}`;
  const response = await tempoApi().get(url).json<WorkIntegrationInstance[]>();
  return response;
};

const deleteInstance = async (id: number): Promise<void> => {
  await tempoApi().delete(`api/v1/work-integrations/instances/${id}`);
};

const getInstanceFieldMappings = async (
  instanceId: number,
  projectId?: string,
): Promise<InstanceFieldMapping[]> => {
  const searchParams = new URLSearchParams();
  if (projectId) {
    searchParams.append('projectId', projectId);
  }
  const query = searchParams.toString();
  const url = `api/v1/work-integrations/instances/${instanceId}/field-mappings${query ? `?${query}` : ''}`;
  return await tempoApi().get(url).json<InstanceFieldMapping[]>();
};

const refreshInstanceFieldMappingAvailability = async (
  instanceId: number,
  ignoreDebounce = false,
): Promise<void> => {
  const query = ignoreDebounce ? '?ignoreDebounce=true' : '';
  await tempoApi().post(
    `api/v1/work-integrations/instances/${instanceId}/field-mappings/refresh-availability${query}`,
  );
};

const createInstanceFieldMapping = async (
  instanceId: number,
  payload: CreateInstanceFieldMappingRequest,
): Promise<InstanceFieldMapping> => {
  return await tempoApi()
    .post(`api/v1/work-integrations/instances/${instanceId}/field-mappings`, { json: payload })
    .json<InstanceFieldMapping>();
};

const deleteInstanceFieldMapping = async (instanceId: number, mappingId: number): Promise<void> => {
  await tempoApi().delete(
    `api/v1/work-integrations/instances/${instanceId}/field-mappings/${mappingId}`,
  );
};

const updateInstanceFieldMapping = async (
  instanceId: number,
  mappingId: number,
  payload: UpdateInstanceFieldMappingRequest,
): Promise<InstanceFieldMapping> => {
  return await tempoApi()
    .put(`api/v1/work-integrations/instances/${instanceId}/field-mappings/${mappingId}`, {
      json: payload,
    })
    .json<InstanceFieldMapping>();
};

const getInstanceFieldMappingsUsage = async (instanceId: number): Promise<FieldMappingUsage[]> => {
  return await tempoApi()
    .get(`api/v1/work-integrations/instances/${instanceId}/field-mappings/usage`)
    .json<FieldMappingUsage[]>();
};

const getFieldValueMappings = async (
  instanceFieldMappingId: number,
): Promise<FieldValueMapping[]> => {
  return await tempoApi()
    .get(
      `api/v1/work-integrations/instance-field-mappings/${instanceFieldMappingId}/value-mappings`,
    )
    .json<FieldValueMapping[]>();
};

const getLoopFieldValues = async (instanceFieldMappingId: number): Promise<LoopFieldValue[]> => {
  return await tempoApi()
    .get(`api/v1/work-integrations/instance-field-mappings/${instanceFieldMappingId}/loop-values`)
    .json<LoopFieldValue[]>();
};

const getJiraFieldValues = async (
  instanceFieldMappingId: number,
): Promise<JiraFieldValue[] | null> => {
  const response = await tempoApi().get(
    `api/v1/work-integrations/instance-field-mappings/${instanceFieldMappingId}/jira-values`,
    { timeout: 60_000 },
  );
  if (response.status === 202) return null;
  return response.json<JiraFieldValue[]>();
};

const retryJiraFieldValueDiscovery = async (instanceFieldMappingId: number): Promise<void> => {
  await tempoApi().post(
    `api/v1/work-integrations/instance-field-mappings/${instanceFieldMappingId}/jira-values/retry`,
  );
};

const saveFieldValueMappings = async (
  instanceFieldMappingId: number,
  payload: SaveFieldValueMappingsRequest,
): Promise<FieldValueMapping[]> => {
  return await tempoApi()
    .put(
      `api/v1/work-integrations/instance-field-mappings/${instanceFieldMappingId}/value-mappings`,
      { json: payload },
    )
    .json<FieldValueMapping[]>();
};

const getFieldMappingSummary = async (
  instanceFieldMappingId: number,
): Promise<FieldMappingSummary> =>
  tempoApi()
    .get(`api/v1/work-integrations/instance-field-mappings/${instanceFieldMappingId}/summary`)
    .json<FieldMappingSummary>();

export const IntegrationInstancesAPI = {
  createInstance,
  getInstances,
  deleteInstance,
  getInstanceFieldMappings,
  refreshInstanceFieldMappingAvailability,
  createInstanceFieldMapping,
  deleteInstanceFieldMapping,
  updateInstanceFieldMapping,
  getInstanceFieldMappingsUsage,
  getFieldValueMappings,
  getLoopFieldValues,
  getJiraFieldValues,
  retryJiraFieldValueDiscovery,
  saveFieldValueMappings,
  getFieldMappingSummary,
} as const;
